import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Award,
  Phone,
  Users,
  Mail,
  AlertTriangle,
  PiggyBank,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { MappingIncompleteBanner } from '../../components/broker-activity/MappingIncompleteBanner';
import { useBrokerScorecard } from '../../hooks/useBrokerScorecard';
import { formatCompactCurrency } from '../../utils/formatters';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// Mockup mycrm-integration §Broker Scorecard / Adviser Productivity.
// Per-broker table with activity + deals + commissions + alerts.

const fmtDollars = (cents: number): string => formatCompactCurrency(cents);

const ScorecardPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  // Marketing sees the page but NOT commission-derived money fields. The backend
  // zeroes settled commission, active trail book, and open trail-leakage alerts,
  // so hide those columns/cards rather than render zeroed money (marketing RBAC §4).
  const isMarketing = userData?.role === 'marketing';
  const [windowDays, setWindowDays] = useState<number>(7);

  const { data, loading, error, refresh } = useBrokerScorecard(
    companyId,
    windowDays
  );

  const scorecards = data?.scorecards ?? [];
  const hasData = scorecards.length > 0;

  // Compute totals for the summary row.
  const totals = useMemo(() => {
    return scorecards.reduce(
      (acc, s) => ({
        calls: acc.calls + s.callsThisWindow,
        meetings: acc.meetings + s.meetingsThisWindow,
        emails: acc.emails + s.emailsThisWindow,
        activeDeals: acc.activeDeals + s.activeDealCount,
        settledThisMonth: acc.settledThisMonth + s.settledThisMonth,
        commissionCents:
          acc.commissionCents + s.settledCommissionCentsThisMonth,
        trailBookCents: acc.trailBookCents + s.activeTrailBookCents,
        openAlerts: acc.openAlerts + s.openTrailLeakageAlerts,
      }),
      {
        calls: 0,
        meetings: 0,
        emails: 0,
        activeDeals: 0,
        settledThisMonth: 0,
        commissionCents: 0,
        trailBookCents: 0,
        openAlerts: 0,
      }
    );
  }, [scorecards]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Award}
        title="Team Scorecard"
        subtitle="Per-person performance: activity, deals, settlements, commissions, and alert coverage"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      <MappingIncompleteBanner />

      {/* Activity window selector */}
      <div className="flex items-center gap-3 text-sm">
        <label className="text-gray-400">Activity window</label>
        <select
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        >
          <option value={7}>7 days (weekly)</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days (monthly)</option>
          <option value={90}>90 days</option>
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Company-wide totals */}
      <div
        className={`grid grid-cols-2 gap-4 ${
          isMarketing ? 'md:grid-cols-2' : 'md:grid-cols-4'
        }`}
      >
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-blue-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400 inline-flex items-center gap-1">
            <Phone className="w-3 h-3" />
            Calls ({windowDays}d)
          </div>
          <div className="mt-1 text-3xl font-bold text-blue-400">
            {totals.calls.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            {totals.meetings.toLocaleString()} meetings ·{' '}
            {totals.emails.toLocaleString()} emails
          </div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-purple-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Active deals
          </div>
          <div className="mt-1 text-3xl font-bold text-purple-400">
            {totals.activeDeals.toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            in pipeline right now
          </div>
        </div>
        {/* Commission-derived cards hidden for marketing (backend zeroes them). */}
        {!isMarketing && (
          <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-emerald-500 p-4">
            <div className="text-[11px] uppercase tracking-wider text-gray-400 inline-flex items-center gap-1">
              <PiggyBank className="w-3 h-3" />
              Active trail book
            </div>
            <div className="mt-1 text-3xl font-bold text-emerald-400">
              {fmtDollars(totals.trailBookCents)}
            </div>
            <div className="mt-1 text-[11px] text-gray-500">
              {totals.settledThisMonth} settlements this month
            </div>
          </div>
        )}
        {!isMarketing && (
          <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-amber-500 p-4">
            <div className="text-[11px] uppercase tracking-wider text-gray-400 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Open trail alerts
            </div>
            <div className="mt-1 text-3xl font-bold text-amber-400">
              {totals.openAlerts.toLocaleString()}
            </div>
            <div className="mt-1 text-[11px] text-gray-500">
              across all brokers
            </div>
          </div>
        )}
      </div>

      {!loading && !hasData && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <Award className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-white mb-2">
            No scorecard data yet
          </h3>
          {isMarketing ? (
            <p className="text-xs text-gray-400 max-w-md mx-auto">
              Scorecards combine MyCRM deals, activity logs, and broker
              performance data. Ask an admin to connect your CRM and map
              advisers to populate this page.
            </p>
          ) : (
            <p className="text-xs text-gray-400 max-w-md mx-auto">
              Scorecards combine MyCRM deals, activity logs, commission data,
              and trail-leakage alerts. Connect MyCRM via{' '}
              <a
                href="/leads/setup"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Setup Wizard
              </a>{' '}
              and map advisers in{' '}
              <a
                href="/settings/crm-mappings"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                CRM Mappings
              </a>{' '}
              to populate this page.
            </p>
          )}
        </div>
      )}

      {/* Per-broker table */}
      {hasData && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white">
              Broker performance ({scorecards.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900/50 text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Broker</th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Phone className="inline w-3 h-3 mr-1" />
                    Calls
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Users className="inline w-3 h-3 mr-1" />
                    Meetings
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Mail className="inline w-3 h-3 mr-1" />
                    Emails
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    Active deals
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    Settled this mo.
                  </th>
                  {/* Commission-derived columns hidden for marketing (backend zeroes them). */}
                  {!isMarketing && (
                    <th className="px-4 py-3 text-right font-medium">
                      Commission this mo.
                    </th>
                  )}
                  {!isMarketing && (
                    <th className="px-4 py-3 text-right font-medium">
                      Trail book
                    </th>
                  )}
                  {!isMarketing && (
                    <th className="px-4 py-3 text-right font-medium">
                      <AlertTriangle className="inline w-3 h-3 mr-1" />
                      Alerts
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {scorecards.map((s) => (
                  <tr key={s.adviserId} className="text-gray-200">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">
                        {s.adviserName ?? s.adviserId}
                      </div>
                      {s.adviserEmail && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {s.adviserEmail}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {s.callsThisWindow.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {s.meetingsThisWindow.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {s.emailsThisWindow.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {s.activeDealCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {s.settledThisMonth.toLocaleString()}
                    </td>
                    {/* Commission-derived cells hidden for marketing (backend zeroes them). */}
                    {!isMarketing && (
                      <td className="px-4 py-3 text-right font-mono text-emerald-400 font-semibold">
                        {fmtDollars(s.settledCommissionCentsThisMonth)}
                      </td>
                    )}
                    {!isMarketing && (
                      <td className="px-4 py-3 text-right font-mono">
                        {fmtDollars(s.activeTrailBookCents)}
                      </td>
                    )}
                    {!isMarketing && (
                      <td className="px-4 py-3 text-right font-mono">
                        {s.openTrailLeakageAlerts > 0 ? (
                          <span className="text-amber-400 font-bold">
                            {s.openTrailLeakageAlerts}
                          </span>
                        ) : (
                          <span className="text-gray-500">0</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorecardPage;
